/**
 * Non-destructive sample + slice normalization helpers.
 *
 * The audio bytes on disk (local IndexedDB Blob, or S3 object for
 * cloud samples) are never touched. Normalize computes a peak
 * analysis of the raw buffer, derives the dB adjustment needed to
 * reach the target peak level, and writes that adjustment to the
 * row's `gainDb` field. The trigger path (`scheduleSample` +
 * `scheduleGranular` in `useAudioEngine.ts`) multiplies this
 * stored gain into the voice's final gain node, so "normalize"
 * just becomes "here's a sensible starting gain."
 *
 * Rationale for non-destructive: destructive normalize would
 * require re-uploading the asset to S3 (or re-writing the
 * IndexedDB blob) on every change, losing the original reference
 * material. Keeping the gain in metadata means undo is free and
 * the user can A/B compare without a round-trip.
 */

/** Default normalize target. -3 dBFS leaves headroom for subsequent
 *  velocity modulation + downstream FX without clipping the master. */
export const DEFAULT_NORMALIZE_TARGET_DBFS = -3

/** Linear peak amplitude across all channels + frames.
 *  Returns 0 for silent buffers. Walks Float32Arrays directly
 *  for speed on long samples. */
export function analyzePeak(
  buffer: AudioBuffer,
  startSec?: number,
  endSec?: number,
): number {
  const sr = buffer.sampleRate
  const start = Math.max(0, Math.floor((startSec ?? 0) * sr))
  const end =
    typeof endSec === 'number'
      ? Math.min(buffer.length, Math.floor(endSec * sr))
      : buffer.length
  if (end <= start) return 0
  let peak = 0
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const data = buffer.getChannelData(c)
    for (let i = start; i < end; i++) {
      const v = data[i]
      const abs = v < 0 ? -v : v
      if (abs > peak) peak = abs
    }
  }
  return peak
}

/** Convert linear gain to dB. Handles the 0-linear case by
 *  returning -Infinity (truly silent). */
export function linearToDb(linear: number): number {
  if (linear <= 0) return -Infinity
  return 20 * Math.log10(linear)
}

/** Convert dB to linear gain. 0 dB → 1.0, -6 dB → ~0.501, etc. */
export function dbToLinear(db: number): number {
  if (!Number.isFinite(db)) return 0
  return Math.pow(10, db / 20)
}

/** Compute the gainDb + peak metadata for a normalize action.
 *  Returns `null` when the buffer is silent (nothing to normalize).
 *  The caller writes these fields back onto the Sample / slice. */
export function computeNormalizeGain(
  buffer: AudioBuffer,
  targetDbFS: number = DEFAULT_NORMALIZE_TARGET_DBFS,
  startSec?: number,
  endSec?: number,
): { gainDb: number; normalizedPeak: number } | null {
  const peak = analyzePeak(buffer, startSec, endSec)
  if (peak <= 0) return null
  const targetLinear = dbToLinear(targetDbFS)
  const gainLinear = targetLinear / peak
  return {
    gainDb: linearToDb(gainLinear),
    normalizedPeak: peak,
  }
}
